/* eslint-disable no-console */
import { Helmet } from "react-helmet";
import { useHistory } from "react-router";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useLazyQuery, gql, useMutation } from "@apollo/client";
import { isValid, differenceInDays } from "date-fns";
import Dropzone from "react-dropzone";
import * as XLSX from "xlsx";
import classNames from "classnames";
import Layout from "../../../components/Layout";
import Table from "../../../components/Table";
import { Heading, Text } from "../../../components/Type";
import styles from "./index.module.scss";
import { FileUpload } from "../../../components/Icons/files";
import { StatusTextCell, ClientCell } from "../../../components/Table/TableCells";
import EmptyView from "../../../components/Table/EmptyView";
import Button from "../../../components/Button";
import { ArrowRight } from "../../../components/Icons/general-interface-1";
import { ClientFileType } from "../../../types/clientFileType";
import Loader from "../../../components/Loader";
import { AlertCircle } from "../../../components/Icons/general-interface-2";
import Modal from "../../../components/Modal";
import ModalActions from "../../../components/ModalActions";
import NotificationBanner from "../../../components/NotificationBanner";
import { postSigningStatusList } from "../../../utils/status";

const GET_MINISTRY_IMPORT_CLIENTS = gql`
  query Clients(
    $limit: Int
    $sort: String
    $start: Int
    $where: JSON
    $connectionWhere: JSON
  ) {
    clientsForMinistryImport(
      limit: $limit
      sort: $sort
      start: $start
      where: $where
    ) {
      id
      first_name
      last_name
      health_card
      client_ref
      active_file {
        id
        status
      }
    }
    clientsConnection(where: $connectionWhere) {
      aggregate {
        count
      }
    }
  }
`;

const UPDATE_STATUS = gql`
  mutation UpdateStatus($input: [UpdateStatusInput]) {
    updateStatus(input: $input) {
      message
    }
  }
`;

type ClientType = {
  id: string;
  first_name: string;
  last_name: string;
  health_card?: string;
  client_ref: string;
  active_file: ClientFileType;
  new_status: string;
};

type UploadType = {
  client_ref: string;
  new_status: string;
};

const StatusReport = () => {
  const history = useHistory();

  // controls components visibility
  const [fileData, setFileData] = useState(false);
  const [finished, setFinished] = useState(false);
  const [isValidDate, setValidDate] = useState(true);
  const [validMessage, setValidMessage] = useState("");

  // store data uploaded from file, merged with clients and pagination
  const [uploadData, setUploadData] = useState<UploadType[]>([]);
  const [clientData, setClientData] = useState<{
    clients: ClientType[];
    count: number;
  }>({ clients: [], count: 0 });
  const [slicedData, setSlicedData] = useState<ClientType[]>([]);
  const [pageSize, setPageSize] = useState(10);

  const [error, setError] = useState<unknown>();
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isValidModalOpen, setValidModalOpen] = useState(false);
  const [, setBoundaryError] = useState<Error | null>(null);

  // controls the number of records read from file
  const [totalData, setTotalData] = useState(0);

  // controls the number of records matched to EASI
  const [totalMatched, setTotalMatched] = useState(0);

  // fetch client data and merge with uploaded data. Maybe data is not in same order
  const [getClients, { data, loading: loadingData }] = useLazyQuery(
    GET_MINISTRY_IMPORT_CLIENTS,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "no-cache",
      onCompleted: () => {
        if (data) {
          setTotalMatched(data.clientsForMinistryImport.length);
          const merged: ClientType[] = [];
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < data.clientsForMinistryImport.length; i++) {
            if (
              postSigningStatusList.find(
                (x) =>
                  x.value ===
                  data.clientsForMinistryImport[i].active_file.status
              )
            ) {
              const item = uploadData?.find(
                (x) =>
                  x.client_ref ===
                    data.clientsForMinistryImport[i].client_ref &&
                  x.new_status !==
                    data.clientsForMinistryImport[i].active_file.status
              );
              if (item) {
                merged.push({
                  ...data.clientsForMinistryImport[i],
                  ...item,
                });
              }
            }
          }
          setFileData(true);
          setClientData({ clients: merged, count: merged.length });
        }
      },
      onError: () => {
        setFileData(false);
        setFinished(false);
        setError("Error while merging client data.");
        return null;
      },
    }
  );

  // handle file drop or select
  const handleDrop = (acceptedFiles) => {
    // clear controls visibility and client data
    setSlicedData([]);
    setClientData({ clients: [], count: 0 });
    setUploadData([]);
    setFinished(false);
    setFileData(false);
    setError(undefined);
    setTotalData(0);
    setTotalMatched(0);
    setValidDate(true);

    if (acceptedFiles.length > 1) {
      setError("Only 1 file is accepted.");
      return;
    }

    const file = acceptedFiles[0];
    const allowed = [
      "application/vnd.ms-excel",
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (!allowed.includes(file.type)) {
      setError("File must be .csv or .xlsx format.");
      return;
    }

    const reader = new FileReader();
    reader.onabort = () => setError("Error reading file.");
    reader.onerror = () => setError("Error reading file");
    reader.onload = () => {
      const bstr = reader.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_csv(ws);
      const rows = data.slice(0).split("\n");
      const newArray: UploadType[] = [];
      let validDate = false;
      setValidMessage(
        "The Generated Date was not found in the file. Please note that this will overwrite existing application statuses on Motion EASI. Would you like to proceed?"
      );

      let total = 0;
      rows.forEach((row) => {
        const values = row.split(
          process.env.REACT_APP_CSV_DELIMITER
            ? process.env.REACT_APP_CSV_DELIMITER
            : ","
        );

        // try to parse generated date for comparison.
        // Premise: the line appears in file as "Generated Date: Dec 11, 2022",,,,,,,,
        // The default delimiter is the comma
        if (values[0].includes("Generated Date:")) {
          // remove the Generated Date: string from first split string (month and day)
          const first = values[0].replace(/"Generated Date: /, "");
          // gets the first 5 characters from second split string (year).
          const second = values[1].slice(0, 5);
          // concatenate first and second string (month, day and year)
          const str = `${first},${second}`;
          // try to parse as date and compare
          const date = new Date(str);
          const days = process.env.REACT_APP_STATUS_REPORT_DAYS
            ? Number(process.env.REACT_APP_STATUS_REPORT_DAYS)
            : 5;
          if (isValid(date) && differenceInDays(new Date(), date) < days) {
            validDate = true;
          } else if (differenceInDays(new Date(), date) >= days) {
            validDate = false;
            setValidMessage(
              "The file you are uploading is more than 5 days old. Please note that this will overwrite existing application statuses on Motion EASI. Would you like to proceed?"
            );
          }
        }

        if (values[0].length === 4 && values[1].length === 4 && values[5]) {
          // convert status
          let newStatus = values[5];
          if (newStatus.toUpperCase() === "IN PROCESS") newStatus = "Submitted";
          else if (newStatus.toUpperCase() === "NOT APPROVED")
            newStatus = "Rejected";

          // there is no need to normalize here. Just make sure letters are uppercase
          newArray.push({
            client_ref: values[0].toUpperCase() + values[1],
            new_status: newStatus,
          });

          total += 1;
        }
      });

      // store the number os total records read
      setTotalData(total);

      // if not empty, store data and fetch clients to be merged
      if (newArray.length > 0) {
        setUploadData(newArray);

        if (validDate) {
          setValidDate(true);
          const variables = {
            where: { client_ref_in: newArray.map((x) => x.client_ref) },
            connectionWhere: {
              client_ref_in: newArray.map((x) => x.client_ref),
            },
          };
          getClients({ variables });
        } else {
          setValidDate(false);
        }
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => <ClientCell client={row.original} />,
      },
      {
        Header: "Old Status",
        Cell: ({ row }) => (
          <StatusTextCell value={row.original.active_file.status} />
        ),
      },
      {
        Header: "New Status",
        accessor: "new_status",
        Cell: ({ row }) => <StatusTextCell value={row.original.new_status} />,
      },
    ],
    []
  );

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sortBy }) => {
      const start = pageIndex * pageSize;
      const limit = pageIndex * pageSize + pageSize;

      let sorted: ClientType[] = [];
      if (sortBy.length) {
        if (sortBy[0].id === "first_name") {
          if (sortBy[0].desc)
            sorted = clientData.clients.sort((a, b) =>
              b.first_name > a.first_name ? 1 : -1
            );
          else
            sorted = clientData.clients.sort((a, b) =>
              b.first_name < a.first_name ? 1 : -1
            );
        } else if (sortBy[0].id === "new_status") {
          if (sortBy[0].desc)
            sorted = clientData.clients.sort((a, b) =>
              b.new_status > a.new_status ? 1 : -1
            );
          else
            sorted = clientData.clients.sort((a, b) =>
              b.new_status < a.new_status ? 1 : -1
            );
        }
      } else {
        sorted = clientData.clients;
      }

      setPageSize(pageSize);
      setSlicedData(sorted.slice(start, limit));
    },
    [clientData]
  );

  // update status
  const [updateStatus, { loading: loadingUpdate }] = useMutation(
    UPDATE_STATUS,
    {
      onCompleted: () => {
        setFinished(true);
        setValidDate(true);
      },
    }
  );

  // handle update
  const handleUpdate = () => {
    setConfirmModalOpen(false);
    updateStatus({
      variables: {
        input: clientData.clients.map((x) => {
          return { id: x.active_file.id, status: x.new_status };
        }),
      },
    }).catch((error) => {
      setBoundaryError(() => {
        throw error;
      });
    });
  };

  // handle upload another file
  const handleAnother = () => {
    setError(undefined);
    setSlicedData([]);
    setClientData({ clients: [], count: 0 });
    setUploadData([]);
    setFinished(false);
    setFileData(false);
    setValidDate(true);
    setTotalData(0);
  };

  const pageCount = clientData ? Math.ceil(clientData.count / pageSize) : 0;

  useEffect(() => {
    if (!isValidDate) {
      setValidModalOpen(true);
    }
  }, [isValidDate]);

  const onValidProceed = () => {
    setValidModalOpen(false);
    setValidDate(true);

    const variables = {
      where: { client_ref_in: uploadData.map((x) => x.client_ref) },
      connectionWhere: { client_ref_in: uploadData.map((x) => x.client_ref) },
    };
    getClients({ variables });
  };

  const onValidCancel = () => {
    setValidModalOpen(false);
    setUploadData([]);
    setFileData(false);
    setValidDate(true);
    setTotalData(0);
  };

  return (
    <>
      <Layout>
        <Helmet>
          <title>Motion Home</title>
        </Helmet>
        <div>
          <Heading size="h3" component="h1" className={styles.heading}>
            Import Status Report
          </Heading>
          {((!fileData && !loadingUpdate) || finished) && (
            <Text
              className={styles.importText}
              style={{ fontSize: "18px", lineHeight: "28px" }}
            >
              Upload a ministry report here. It will automatically update all
              status of corresponding client files.
            </Text>
          )}
          {fileData && !loadingUpdate && !finished && (
            <Text
              className={styles.importText}
              style={{ fontSize: "18px", lineHeight: "28px" }}
            >
              {`${totalMatched} applications where matched with EASI records out of ${totalData} total in the report. Of these, ${clientData.count} have a different status.`}
            </Text>
          )}
          {fileData && loadingUpdate && !finished && (
            <Text
              className={styles.importText}
              style={{ fontSize: "18px", lineHeight: "28px" }}
            >
              Please wait. This may take a few minutes.
            </Text>
          )}
        </div>
        {loadingData && (
          <Loader heading="Loading client data..." className={styles.loader} />
        )}
        {loadingUpdate && (
          <Loader heading="Updating status..." className={styles.loader} />
        )}
        {finished && (
          <div>
            <NotificationBanner
              success
              title={`Status updated on ${clientData.count} client file(s).`}
              className={styles.successBanner}
            />
            <div className={styles.footerRow}>
              <Button secondary onClick={handleAnother}>
                Upload Another File
              </Button>
              <Button
                primary
                icon={<ArrowRight />}
                onClick={() => history.replace("/dashboard")}
              >
                Go to dashboard
              </Button>
            </div>
          </div>
        )}
        {!fileData && !loadingUpdate && !loadingData && (
          <Dropzone onDrop={handleDrop}>
            {({ getRootProps, getInputProps }) => (
              <section>
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className={styles.uploadContainer}>
                    <div>
                      <FileUpload className={styles.icon} />
                      <Text>
                        <span>Drag and drop file here or&nbsp;</span>
                        <span className={styles.uploadText}>
                          Upload another file
                        </span>
                      </Text>
                      {error && (
                        <span className={styles.errorText}>
                          <AlertCircle
                            className={classNames(styles.warningIcon)}
                          />
                          {error}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </Dropzone>
        )}
        {fileData && !loadingUpdate && !loadingData && !finished && (
          <div>
            <Table
              data={slicedData}
              columns={columns}
              pageCount={pageCount}
              fetchData={fetchData}
              total={clientData.count}
              initialState={{ sortBy: [{ id: "first_name", desc: false }] }}
              emptyView={
                <EmptyView message="No records to import.">
                  <Button secondary onClick={handleAnother}>
                    Upload another file
                  </Button>
                </EmptyView>
              }
            />
          </div>
        )}
        {clientData.count > 0 && !loadingUpdate && !finished && (
          <div className={styles.footerRow}>
            <Button secondary onClick={handleAnother}>
              Upload Another File
            </Button>
            <Button
              primary
              icon={<ArrowRight />}
              onClick={() => {
                setConfirmModalOpen(true);
              }}
            >
              Update Status
            </Button>
          </div>
        )}
      </Layout>

      <Modal
        isOpen={isConfirmModalOpen}
        title="Update Status"
        onClose={() => {
          setConfirmModalOpen(false);
        }}
      >
        <div className={styles.content}>
          <Text>
            {`The status of ${clientData.count} client files will be updated. The care team members for each team will be notified. Would you like to proceed?`}
          </Text>
        </div>
        <ModalActions>
          <Button primary onClick={handleUpdate}>
            Update
          </Button>
          <Button
            secondary
            onClick={() => {
              setConfirmModalOpen(false);
            }}
          >
            Cancel
          </Button>
        </ModalActions>
      </Modal>

      <Modal
        isOpen={isValidModalOpen}
        title="Warning"
        onClose={() => {
          setValidModalOpen(false);
        }}
      >
        <div className={styles.content}>
          <Text>{validMessage}</Text>
        </div>
        <ModalActions>
          <Button primary onClick={onValidProceed}>
            Proceed
          </Button>
          <Button secondary onClick={onValidCancel}>
            Cancel
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default StatusReport;
